/**
 * MySuggestions — surface 6 (CM's own submissions tracker)
 * (Block 6.C.2 / ADR-021).
 *
 * Any authenticated user (including stewards). Server filters non-stewards
 * to own; here we always pass `suggested_by=me` for clarity (server scopes
 * regardless). Pending rows expand to MySuggestionDetailPane with
 * Withdraw button; disposed rows expand to read-only history.
 */

import { useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTenant } from '@/lib/auth';
import { suggestedEditsApi } from '@/lib/suggestedEdits';
import type { SuggestedEditListResponse, SuggestedEditStatus } from '@/lib/types';
import { AsyncBoundary } from '@/components/primitives/AsyncBoundary';
import { PageShell } from '@/components/primitives/PageShell';
import { SuggestionRow } from '@/components/SuggestionRow';
import { MySuggestionDetailPane } from '@/components/MySuggestionDetailPane';
import { TAB_RAIL_CLASSES } from '@/components/primitives/tabRail';
import { mySuggestionsKey } from '@/lib/refreshFamily';

const VALID_STATUSES: ReadonlyArray<SuggestedEditStatus> = [
  'pending',
  'approved',
  'rejected',
  'withdrawn',
];

const STATUS_LABELS: Record<SuggestedEditStatus, string> = {
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
  withdrawn: 'Withdrawn',
};

export function MySuggestions(): ReactNode {
  const { tenantSlug: routeTenantSlug } = useParams<{ tenantSlug: string }>();
  const { whoami } = useTenant();
  const [searchParams, setSearchParams] = useSearchParams();

  const tenantSlug = routeTenantSlug ?? '';
  const userId = whoami?.user_id ?? null;

  const statusParam = searchParams.get('status');
  const activeStatus: SuggestedEditStatus = VALID_STATUSES.includes(
    statusParam as SuggestedEditStatus,
  )
    ? (statusParam as SuggestedEditStatus)
    : 'pending';

  const invalidationKey = useMemo(
    () => mySuggestionsKey(tenantSlug, userId, activeStatus),
    [tenantSlug, userId, activeStatus],
  );

  const listQuery = useQuery<SuggestedEditListResponse>({
    queryKey: invalidationKey,
    queryFn: ({ signal }) =>
      suggestedEditsApi.list(
        tenantSlug,
        {
          status: activeStatus,
          // Stewards: explicit suggested_by=ctx.user_id to scope to own.
          // Non-stewards: server forces this regardless; we pass it for
          // clarity + symmetry across roles.
          suggested_by: userId ?? undefined,
          limit: 50,
        },
        signal,
      ),
    enabled: userId !== null,
  });

  function handleStatusChange(next: SuggestedEditStatus): void {
    const sp = new URLSearchParams(searchParams);
    sp.set('status', next);
    setSearchParams(sp, { replace: true });
  }

  const rows = listQuery.data?.rows ?? [];

  return (
    <PageShell title="My suggestions" maxWidth="lg">

      <div
        role="tablist"
        aria-label="Filter by status"
        className={`flex gap-1 mb-3 ${TAB_RAIL_CLASSES.tablist}`}
      >
        {VALID_STATUSES.map((status) => {
          const isActive = status === activeStatus;
          return (
            <button
              key={status}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => handleStatusChange(status)}
              className={
                `${TAB_RAIL_CLASSES.tab} ` +
                (isActive
                  ? `border-warm-400 ${TAB_RAIL_CLASSES.active}`
                  : TAB_RAIL_CLASSES.inactive)
              }
            >
              {STATUS_LABELS[status]}
            </button>
          );
        })}
      </div>

      <div className="text-xs text-ink-soft mb-3">
        {listQuery.isSuccess && (
          <>
            {rows.length} {STATUS_LABELS[activeStatus].toLowerCase()}
          </>
        )}
      </div>

      <AsyncBoundary
        isLoading={listQuery.isPending}
        isError={listQuery.isError}
        error={listQuery.error}
        onRetry={() => void listQuery.refetch()}
        isEmpty={listQuery.isSuccess && rows.length === 0}
        emptyHeadline={`No ${STATUS_LABELS[activeStatus].toLowerCase()} suggestions`}
      >
      {listQuery.isSuccess &&
        rows.map((row) => (
          <SuggestionRow
            key={row.id}
            suggestion={row}
            variant="mine"
            renderExpanded={() => (
              <MySuggestionDetailPane
                tenantSlug={tenantSlug}
                suggestion={row}
                invalidationKey={invalidationKey}
              />
            )}
          />
        ))}
      </AsyncBoundary>
    </PageShell>
  );
}
